// ─── Chin Order Executor ───────────────────────────────────
// Immediately-executed order engine for the "Order Now" button.
//
// NOT realtime — it fires exactly when the user sets Buy or Sell
// on the Order button, and runs a sequential loop:
//
//   Loop (iterations = maxOrders - existing positions for symbol):
//     1. Check risk control (spread ≤ 3 pips, single symbol)
//     2. If risk passes → send place-order to cTrader with volume X
//     3. Wait until cTrader confirms the order was opened successfully
//        (poll positions until count increases)
//     4. Continue to next iteration
//
// When the user sets orderDirection back to null, the loop stops.
//
// ABORT MECHANISM: Uses AbortController + flag for immediate,
// unconditional shutdown. The abort() function:
//   1. Sets aborted flag → while-loop exits at next iteration start
//   2. Aborts the AbortController → all pending sleep() calls resolve immediately
//   3. Does NOT cancel in-flight HTTP requests (browser limitation),
//      but the loop will not process their results

export interface OrderLoopConfig {
  accountId: string;
  symbolId: number;
  symbolName: string;
  direction: 'buy' | 'sell';
  volume: number;
  maxOrders: number;
}

export type OrderLoopPhase =
  | 'idle'
  | 'checking_risk'
  | 'placing_order'
  | 'waiting_confirmation'
  | 'completed'
  | 'max_reached'
  | 'blocked'
  | 'error';

export interface OrderLoopStatus {
  symbolId: number;
  phase: OrderLoopPhase;
  currentPositionCount: number;
  maxOrders: number;
  ordersPlaced: number;
  message: string;
  lastError: string | null;
}

/** Result of a single place-order attempt (wrapped in a distributed lock). */
export interface PlaceOrderResult {
  success: boolean;
  error?: string;
  /** 'locked' = another device holds the order lock; 'max_reached' = slots already filled */
  reason?: 'locked' | 'max_reached';
}

export type OnStatusChange = (status: OrderLoopStatus) => void;

// ─── Callback types ────────────────────────────────────────

export interface OrderLoopCallbacks {
  /** Check if risk control allows placing an order. Returns { allowed, reason } */
  checkRisk: () => { allowed: boolean; reason: string };
  /** Place a market order via cTrader. Returns { success, error?, reason? } */
  placeOrder: (params: {
    symbolId: number;
    tradeSide: 'BUY' | 'SELL';
    volume: number;
  }) => Promise<PlaceOrderResult>;
  /** Get the current number of open positions for this symbol from cTrader */
  getPositionCount: () => Promise<number>;
}

// ─── Constants ──────────────────────────────────────────────

const CONFIRMATION_POLL_INTERVAL = 2000; // 2 seconds between position checks
const CONFIRMATION_MAX_WAIT = 20000; // 20 seconds max to wait for cTrader confirmation
const RISK_RETRY_DELAY = 3000; // 3 seconds between risk re-checks when blocked
const ERROR_RETRY_DELAY = 3000; // 3 seconds after an order failure

// ─── Executor ──────────────────────────────────────────────

/**
 * Starts an order execution loop for a single symbol.
 * Runs until all slots are filled (maxOrders reached) or abort is signaled.
 *
 * The returned object has an `abort()` method to stop the loop
 * (used when user clicks Order button again to turn it OFF).
 *
 * ABORT GUARANTEE: Once abort() is called, the loop will NOT:
 *   - Start any new iteration of the while-loop
 *   - Call placeOrder() again
 *   - Wait in any sleep() — they resolve immediately via AbortSignal
 * Any in-flight HTTP request (placeOrder/getPositionCount) may still
 * complete on the network, but its result will be discarded.
 */
export function startOrderLoop(
  config: OrderLoopConfig,
  callbacks: OrderLoopCallbacks,
  onStatus: OnStatusChange,
): { abort: () => void } {
  let aborted = false;
  const abortController = new AbortController();
  const { signal } = abortController;

  const emit = (overrides: Partial<OrderLoopStatus>) => {
    if (aborted) return;
    onStatus({
      symbolId: config.symbolId,
      phase: 'idle',
      currentPositionCount: 0,
      maxOrders: config.maxOrders,
      ordersPlaced: 0,
      message: '',
      lastError: null,
      ...overrides,
    });
  };

  /**
   * Sleep that resolves IMMEDIATELY when abort() is called.
   * Uses AbortSignal instead of polling for cleaner, faster abort.
   */
  const sleep = (ms: number): Promise<void> => {
    if (aborted) return Promise.resolve();
    return new Promise((resolve) => {
      if (signal.aborted) {
        resolve();
        return;
      }
      const timer = setTimeout(resolve, ms);
      const onAbort = () => {
        clearTimeout(timer);
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });
  };

  // ─── Wait for cTrader to confirm the order was opened ────
  // After sending place-order, we poll getPositionCount until
  // it increases by at least 1 (meaning a new position was opened).

  async function waitForConfirmation(
    countBefore: number,
  ): Promise<boolean> {
    const startTime = Date.now();

    while (Date.now() - startTime < CONFIRMATION_MAX_WAIT) {
      if (aborted) return false;

      await sleep(CONFIRMATION_POLL_INTERVAL);
      if (aborted) return false;

      try {
        const currentCount = await callbacks.getPositionCount();
        if (aborted) return false;
        if (currentCount > countBefore) {
          console.log(
            `[OrderExec] ✅ Confirmed: ${config.symbolName} position count ` +
            `${countBefore} → ${currentCount}`
          );
          return true;
        }
      } catch (err) {
        console.warn(`[OrderExec] Position check error during confirmation:`, err);
      }

      emit({
        phase: 'waiting_confirmation',
        currentPositionCount: countBefore,
        message: `Waiting for cTrader to confirm order... (${Math.round((Date.now() - startTime) / 1000)}s)`,
      });
    }

    console.warn(
      `[OrderExec] ⚠️ Confirmation timeout: ${config.symbolName} ` +
      `still has ${countBefore} positions after ${CONFIRMATION_MAX_WAIT / 1000}s`
    );
    return false;
  }

  // ─── Main Loop ────────────────────────────────────────────

  async function run(): Promise<void> {
    let ordersPlaced = 0;

    console.log(
      `[OrderExec] 🚀 Starting order loop: ${config.symbolName} ` +
      `${config.direction.toUpperCase()} vol=${config.volume} max=${config.maxOrders}`
    );

    // ═══ MAIN WHILE LOOP ═══
    // Each iteration checks `aborted` at the top, before EVERY async
    // operation, and after EVERY async operation. This ensures the loop
    // stops as soon as abort() is called, regardless of which step it's in.
    while (!aborted) {
      // ── HARD CAP (safety net): never place more than maxOrders this session ──
      // This is the critical guard against runaway over-ordering. Previously the
      // loop relied ONLY on getPositionCount() (a cached value refreshed every 8s),
      // so a stale/incorrect count caused it to keep placing orders indefinitely
      // (e.g. maxOrders=4 but 22 orders placed).
      if (ordersPlaced >= config.maxOrders) {
        console.log(
          `[OrderExec] ✅ Hard cap reached: ${config.symbolName} ` +
          `${ordersPlaced}/${config.maxOrders} orders placed`
        );
        break;
      }

      // ── Step 1: Get current position count ──
      let currentCount: number;
      try {
        currentCount = await callbacks.getPositionCount();
      } catch (err) {
        console.warn(`[OrderExec] Failed to get positions:`, err);
        if (aborted) break;
        emit({
          phase: 'error',
          currentPositionCount: 0,
          ordersPlaced,
          message: 'Failed to fetch positions',
          lastError: String(err),
        });
        await sleep(ERROR_RETRY_DELAY);
        continue;
      }

      if (aborted) break;

      emit({
        phase: 'checking_risk',
        currentPositionCount: currentCount,
        ordersPlaced,
        message: `${currentCount}/${config.maxOrders} positions`,
      });

      // ── Step 2: Check if max orders already reached ──
      if (currentCount >= config.maxOrders) {
        console.log(
          `[OrderExec] ✅ Max orders reached: ${config.symbolName} ` +
          `${currentCount}/${config.maxOrders}`
        );
        emit({
          phase: 'max_reached',
          currentPositionCount: currentCount,
          ordersPlaced,
          message: `Max orders reached: ${currentCount}/${config.maxOrders}`,
        });
        break;
      }

      // ── Step 3: Check risk control ──
      const risk = callbacks.checkRisk();
      if (!risk.allowed) {
        console.log(
          `[OrderExec] ⛔ Risk blocked: ${config.symbolName} — ${risk.reason}`
        );
        emit({
          phase: 'blocked',
          currentPositionCount: currentCount,
          ordersPlaced,
          message: risk.reason,
        });
        await sleep(RISK_RETRY_DELAY);
        continue;
      }

      if (aborted) break;

      // ── Step 4: Place order ──
      const tradeSide = config.direction === 'buy' ? 'BUY' : 'SELL';
      const remaining = config.maxOrders - currentCount;

      emit({
        phase: 'placing_order',
        currentPositionCount: currentCount,
        ordersPlaced,
        message: `Sending ${tradeSide} order (${ordersPlaced + 1}/${ordersPlaced + remaining})...`,
      });

      // ── Volume conversion: UI shows lots → cTrader expects units (1 lot = 100,000 units) ──
      const ctraderVolume = config.volume * 100000;

      console.log(
        `[OrderExec] 📤 Placing order: ${config.symbolName} ` +
        `${tradeSide} UI_vol=${config.volume} lots → cTrader_vol=${ctraderVolume} units (${ordersPlaced + 1}th attempt)`
      );

      let orderResult: PlaceOrderResult;
      try {
        orderResult = await callbacks.placeOrder({
          symbolId: config.symbolId,
          tradeSide,
          volume: ctraderVolume,
        });
      } catch (err) {
        console.error(`[OrderExec] Order request failed:`, err);
        if (aborted) break;
        emit({
          phase: 'error',
          currentPositionCount: currentCount,
          ordersPlaced,
          message: 'Order request failed',
          lastError: String(err),
        });
        await sleep(ERROR_RETRY_DELAY);
        continue;
      }

      // ═══ POST-PLACEORDER ABORT CHECK ═══
      // If abort was called while placeOrder was in-flight,
      // discard the result and exit immediately.
      if (aborted) {
        console.log(`[OrderExec] 🛑 Aborted during placeOrder — discarding result for ${config.symbolName}`);
        break;
      }

      if (!orderResult.success) {
        // ── MAX_REACHED: another device already filled the remaining slots ──
        // The lock re-check saw the true position count hit maxOrders while we
        // were waiting. Stop cleanly — the loop's own hard cap + step-2 check
        // already bound us, so there is nothing left to place.
        if (orderResult.reason === 'max_reached') {
          console.log(
            `[OrderExec] ✅ Max reached via lock re-check: ${config.symbolName} — ${orderResult.error || 'max orders reached'}`
          );
          emit({
            phase: 'max_reached',
            currentPositionCount: currentCount,
            ordersPlaced,
            message: orderResult.error || 'Max orders reached',
          });
          break;
        }

        // ── LOCKED: another device is placing right now ──
        // Retry shortly — by the next iteration the lock is free and the
        // re-check will see the updated position count.
        if (orderResult.reason === 'locked') {
          console.log(
            `[OrderExec] 🔒 Lock held by another device: ${config.symbolName} — ${orderResult.error || 'locked'}`
          );
          emit({
            phase: 'blocked',
            currentPositionCount: currentCount,
            ordersPlaced,
            message: orderResult.error || 'Another device is placing an order',
          });
          await sleep(RISK_RETRY_DELAY);
          continue;
        }

        console.warn(
          `[OrderExec] ❌ Order rejected: ${config.symbolName} — ${orderResult.error || 'unknown'}`
        );
        emit({
          phase: 'error',
          currentPositionCount: currentCount,
          ordersPlaced,
          message: orderResult.error || 'Order rejected',
          lastError: orderResult.error || null,
        });
        await sleep(ERROR_RETRY_DELAY);
        continue;
      }

      // ── CRITICAL FIX: count the order IMMEDIATELY ──
      // placeOrder returned success, meaning cTrader accepted the order. Increment
      // the local counter NOW (not after waitForConfirmation) so the loop has a
      // hard cap even when getPositionCount() returns stale data. This prevents
      // the runaway over-ordering bug (maxOrders=4 → 22 orders).
      ordersPlaced++;
      console.log(
        `[OrderExec] 📈 Order accepted: ${config.symbolName} ` +
        `(${ordersPlaced}/${config.maxOrders} placed)`
      );

      if (ordersPlaced >= config.maxOrders) {
        console.log(
          `[OrderExec] ✅ Hard cap reached: ${config.symbolName} ` +
          `${ordersPlaced}/${config.maxOrders} orders placed`
        );
        break;
      }

      // ── Step 5: Wait for cTrader confirmation (best-effort logging) ──
      // The order is already counted above, so this only improves visibility.
      // If it times out, we continue safely — the hard cap still bounds the loop.
      console.log(
        `[OrderExec] ⏳ Waiting for confirmation: ${config.symbolName} ` +
        `(current positions: ${currentCount})`
      );

      const confirmed = await waitForConfirmation(currentCount);
      if (aborted) break;

      if (!confirmed) {
        console.warn(
          `[OrderExec] ⚠️ Confirmation timeout: ${config.symbolName} — ` +
          `position count still ${currentCount}. Order may still have opened; ` +
          `continuing if slots remain.`
        );
      }

      // ── Step 6: Small delay before next iteration ──
      await sleep(1000);
    }

    if (aborted) {
      console.log(`[OrderExec] 🛑 Aborted: ${config.symbolName}`);
      emit({
        phase: 'idle',
        currentPositionCount: 0,
        ordersPlaced,
        message: 'Cancelled',
      });
    } else {
      console.log(
        `[OrderExec] ✅ Loop completed: ${config.symbolName} ` +
        `(${ordersPlaced} orders placed)`
      );
      emit({
        phase: 'completed',
        currentPositionCount: config.maxOrders,
        ordersPlaced,
        message: `Done: ${ordersPlaced} order(s) placed`,
      });
    }
  }

  // Fire and forget — the loop runs independently
  run();

  return {
    abort: () => {
      if (aborted) return; // Idempotent
      aborted = true;
      abortController.abort();
      console.log(`[OrderExec] 🛑 Abort signal sent for ${config.symbolName} (aborted + signal)`);
    },
  };
}